import { useState, useEffect, useCallback } from 'react';
import { useDataProvider } from 'react-admin';

export interface ReportFilters {
  start_date: string;
  end_date: string;
  site_id?: string;
  vendor_id?: string;
  charger_id?: string;
  group_by?: string;
  [key: string]: any;
}

export interface UseReportDataOptions {
  endpoint: string;
  filters: ReportFilters;
  enabled?: boolean;
}

export interface UseReportDataResult<T = any> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook for fetching report data from backend
 *
 * @example
 * const { data, loading, error, refetch } = useReportData({
 *   endpoint: '/api/backend/reports/revenue',
 *   filters: {
 *     start_date: '2024-01-01',
 *     end_date: '2024-01-31',
 *     site_id: 'site123'
 *   }
 * });
 */
export const useReportData = <T = any>({
  endpoint,
  filters,
  enabled = true,
}: UseReportDataOptions): UseReportDataResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const dataProvider = useDataProvider();

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query string from filters
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const fullUrl = `${endpoint}?${params.toString()}`;

      // Use dataProvider's custom fetch
      const response = await dataProvider.customFetch(fullUrl, {
        method: 'GET',
      });

      setData(response);
    } catch (err: any) {
      console.error('Report data fetch error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [endpoint, JSON.stringify(filters), enabled, dataProvider]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
};
